// Language selector behaviour, shared by header / drawer / footer.
// URLs come from snippets/market-url.liquid, this file just marks the
// current market, carries the query where safe and handles the clicks.
//
// markup contract:
//   container:  [data-market-switcher] (+ optional [data-market-active-class])
//   entries:    [data-market-url="<destination>"] on <a>, <option>, whatever
(function () {
  'use strict';

  var DEFAULT_ACTIVE_CLASS = 'is-current-market';

  // origin + path without trailing slash = "same page"
  function pageKey(url) {
    return url.origin + url.pathname.replace(/\/$/, '');
  }

  // The theme only writes ?variant= into the URL when the shopper *changes* the
  // selection — product-info.js and global.js both call replaceState from their
  // change handlers, never on load. Someone landing straight on a product page
  // therefore has a bare URL, and copying window.location.search alone would
  // drop the variant they're looking at until they touch the picker once.
  // Read the selection out of the DOM instead. Same-store only (the caller
  // guards on origin): the .com markets share one catalogue, so the ids match.
  function currentVariantId() {
    if (window.location.pathname.indexOf('/products/') === -1) return null;

    var host =
      document.querySelector('product-info[id^="MainProduct"]') ||
      document.querySelector('product-info');
    if (!host) return null;

    // the id input inside #product-form-<section> is the one product-info.js
    // keeps in sync — other copies on the page (installment, sticky bars,
    // upsells) can be stale or belong to a different product
    var section = host.getAttribute('data-section');
    var form = section && document.getElementById('product-form-' + section);
    var input =
      (form && form.querySelector('input[name="id"]')) ||
      host.querySelector('input.product-variant-id[name="id"]');

    var id = input && input.value && input.value.trim();
    return id && /^\d+$/.test(id) ? id : null;
  }

  // returns: url string -> navigate | null -> current market | undefined -> bad entry
  // null vs undefined matters: don't mark a broken menu entry as current
  function resolve(rawUrl) {
    // empty attr = market-url.liquid rejected the menu entry
    if (!rawUrl) return undefined;

    var target;

    try {
      target = new URL(rawUrl, window.location.href);
    } catch (e) {
      return undefined;
    }

    // carry the query only same-origin, other stores don't share variant ids etc
    if (target.origin === window.location.origin) {
      target.search = window.location.search;
      // filter values are translated per language, a carried filter can 0-result
      // the collection. ?country= is Shopify's market override and would bounce
      // the shopper straight back to the market they're leaving, whatever the
      // code. drop both, keep variant/sort_by/q/page/...
      var dropped = [];
      target.searchParams.forEach(function (value, key) {
        if (key.indexOf('filter.') === 0 || key === 'country') dropped.push(key);
      });
      dropped.forEach(function (key) {
        target.searchParams.delete(key);
      });

      // URL wins when it already names a variant; otherwise fall back to the
      // one currently selected on the page
      if (!target.searchParams.has('variant')) {
        var variantId = currentVariantId();
        if (variantId) target.searchParams.set('variant', variantId);
      }
    }

    if (pageKey(target) === pageKey(window.location)) return null;

    return target.href;
  }

  var SCROLL_KEY = 'market-scroll-y';

  // keep scroll position across the switch. same-origin only — sessionStorage
  // is per-origin, a .ch hop could never read it and the stale key would
  // misfire later
  function saveScroll(href) {
    try {
      if (new URL(href, window.location.href).origin !== window.location.origin) return;
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    } catch (e) {
      // storage blocked (private mode) — switching still works
    }
  }

  function restoreScroll() {
    var stored;
    try {
      stored = sessionStorage.getItem(SCROLL_KEY);
      if (stored === null) return;
      sessionStorage.removeItem(SCROLL_KEY); // only once
    } catch (e) {
      return;
    }
    var y = parseInt(stored, 10);
    if (isNaN(y)) return;
    window.scrollTo(0, y);
    // page often isn't full height yet at DOMContentLoaded, retry once on load
    if (window.scrollY < y) {
      window.addEventListener(
        'load',
        function () {
          window.scrollTo(0, y);
        },
        { once: true }
      );
    }
  }

  function init(root) {
    var entries = root.querySelectorAll('[data-market-url]');
    if (!entries.length) return;

    var activeClass =
      root.getAttribute('data-market-active-class') || DEFAULT_ACTIVE_CLASS;

    Array.prototype.forEach.call(entries, function (entry) {
      var raw = entry.getAttribute('data-market-url');

      // destination == current page -> this is the active market
      if (resolve(raw) === null) {
        entry.classList.add(activeClass);
        entry.setAttribute('aria-current', 'true');
        if (entry.tagName === 'OPTION') entry.selected = true;
      }

      // options are handled via the select change below
      if (entry.tagName === 'OPTION') return;

      entry.addEventListener('click', function (event) {
        var href = resolve(raw);

        // bad entry: let the <a> do its native thing
        if (href === undefined) return;

        event.preventDefault();

        // already here
        if (href === null) return;

        saveScroll(href);
        window.location.href = href;
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('select'), function (select) {
      select.addEventListener('change', function () {
        var option = this.options[this.selectedIndex];
        var raw = option && option.getAttribute('data-market-url');
        if (!raw) return;

        var href = resolve(raw);
        if (typeof href === 'string') {
          saveScroll(href);
          window.location.href = href;
        }
      });
    });
  }

  function start() {
    restoreScroll();
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-market-switcher]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
